//@flow
import {
    personnelMaster as personnelApi,
    bulkPersonnelFetch as bulkPersonnelFetchApi,
    personnelStatusUpdate as personnelStatusUpdateApi,
    bulkDisablePersonnel as bulkDisablePersonnelApi,
    resetPersonnelPassword as resetPersonnelPasswordApi,
    unblockPersonnel as unblockPersonnelApi,
    validateEntityIds as validateEntityIdsApi
} from 'common/middleware/api';
import { showToast } from 'common/redux/actions/toast';
import { ToastTypes } from 'common/components/core/toast/Toast';
import PaginatedEntityActions from 'common/redux/actions/PaginatedEntityActions';
import I18nHelper from 'common/helpers/I18nHelper';
import { injectError } from 'common/redux/actions/error';
import { ApiError } from 'common/modules/api';

import type { MasterPersonnel } from 'common/models/internal/MasterPersonnel';
import type { Dispatch } from 'redux';
import { PersonnelStatusEnum } from 'common/models/internal/MasterPersonnel';
import { ENTITY_NAME_ID } from 'common/constants/entity/personnelMaster';

const translationsNamespace = ['entityManagement'];
const nsTranslate = I18nHelper.getTranslate(translationsNamespace);

type Params = {
    clientId: string,
    personnelId?: string
};

class PersonnelActions extends PaginatedEntityActions<MasterPersonnel, Params> {
    entityNameId = ENTITY_NAME_ID;
    superAddEntity = this.addEntity;

    addEntity = (params: Params, entity: Personnel, _options = {}) => {
        const message = params.overwrite
            ? 'savedChangesMessage'
            : 'addEntitySuccessMessage';
        return (dispatch: Dispatch<*>) => {
            return new Promise((resolve, reject) => {
                const options = {
                    onSuccess: (personnel: MasterPersonnel) => {
                        const toast = {
                            message: nsTranslate(message, {
                                entityNameId: 'personnel'
                            }),
                            type: ToastTypes.Success
                        };
                        dispatch(showToast(toast, params));
                        resolve(personnel);
                    },
                    onFailure: (error: Error) => {
                        reject(error);
                    },
                    dontInjectError: true
                };
                dispatch(this.superAddEntity(params, entity, options));
            });
        };
    };

    entityStatusUpdate = this.entityStatusUpdate;

    disableEntity = (params: Params, _options = {}) => {
        return (dispatch: Dispatch<*>) => {
            return new Promise((resolve, reject) => {
                const options = {
                    onSuccess: (personnel: MasterPersonnel) => {
                        const toast = {
                            message: nsTranslate(
                                'disableEntitySuccessMessage',
                                {
                                    entityNameId: 'personnel'
                                }
                            ),
                            type: ToastTypes.Success
                        };
                        dispatch(showToast(toast, params));
                        resolve(personnel);
                    },
                    onFailure: (error: Error) => {
                        reject(error);
                    }
                };
                const body = {
                    status: PersonnelStatusEnum.Inactive
                };
                dispatch(this.entityStatusUpdate(params, options, body));
            });
        };
    };

    enableEntity = (params: Params, _options = {}) => {
        return (dispatch: Dispatch<*>) => {
            return new Promise((resolve, reject) => {
                const options = {
                    onSuccess: (personnel: MasterPersonnel) => {
                        const toast = {
                            message: nsTranslate('enableEntitySuccessMessage', {
                                entityNameId: 'personnel'
                            }),
                            type: ToastTypes.Success
                        };
                        dispatch(showToast(toast, params));
                        resolve(personnel);
                    },
                    onFailure: (error: Error) => {
                        reject(error);
                    }
                };
                const body = {
                    status: PersonnelStatusEnum.Active
                };
                dispatch(this.entityStatusUpdate(params, options, body));
            });
        };
    };

    resetPassword = (params: Params): Function => {
        return (dispatch: Dispatch<$FixMe>): Promise<any> => {
            return resetPersonnelPasswordApi
                .post(params, {})
                .then((response: any): Object => {
                    const toast = {
                        message: nsTranslate('resetPasswordSuccessMessage', {
                            entityNameId: this.entityNameId
                        }),
                        type: ToastTypes.Success
                    };
                    dispatch(showToast(toast, params));
                    return response;
                })
                .catch((error: Error) => {
                    if (error instanceof ApiError) {
                        dispatch(injectError(error));
                        return error;
                    }
                    throw error;
                });
        };
    };

    unblock = (params: { clientId: string, personnelId: string }) => {
        return (dispatch: Dispatch<$FixMe>) => {
            return unblockPersonnelApi
                .post(params, {})
                .then(() => {
                    dispatch(
                        showToast({
                            message: nsTranslate(
                                'unblockPersonnelSuccessMessage'
                            ),
                            type: ToastTypes.Success
                        })
                    );
                })
                .catch(error => {
                    if (error instanceof ApiError) {
                        dispatch(injectError(error));
                        return error;
                    }
                    throw error;
                });
        };
    };
}

export default new PersonnelActions(
    'Personnel',
    personnelApi,
    bulkPersonnelFetchApi,
    null,
    bulkDisablePersonnelApi,
    validateEntityIdsApi,
    null,
    personnelStatusUpdateApi,
    resetPersonnelPasswordApi
);
